import React, { useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleUser,
  faFolderOpen,
  faSignal,
  faTag,
} from '@awesome.me/kit-a6e51d954f/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from '@/components/ui/button';
import { DialogClose, DialogFooter } from '@/components/ui/dialog';
import { Form as ShadcnForm } from '@/components/ui/form';
import { Alert } from '../../../../../../shared/components/Alert';
import {
  InputField,
  MultiSelectField,
  SelectChip,
  SubmitButton,
  TextareaField,
} from '../../../../../../shared/components/Form';
import { useAuth } from '../../../../../../shared/hooks';
import { peopleOptions } from '../../../../../../shared/utils';
import { TagSwatch } from '../../../../../shared/components/Tag';
import { formShape } from '../../../../../shared/types';
import { ERROR_TITLES, TASK_PRIORITIES } from '../../../constants';
import { useBoard, useDialogs, useResources } from '../../../hooks';

const alertIcon = <FontAwesomeIcon icon={faCircleExclamation} />;
const assigneeIcon = <FontAwesomeIcon icon={faCircleUser} />;
const caseIcon = <FontAwesomeIcon icon={faFolderOpen} />;
const priorityIcon = <FontAwesomeIcon icon={faSignal} />;
const statusIcon = <FontAwesomeIcon icon={faCircleCheck} />;
const tagIcon = <FontAwesomeIcon icon={faTag} />;

const Form = ({ errorMessage, form, isSubmitting, onSubmit }) => {
  const { user: currentUser } = useAuth();
  const { isLoading: areStatusesLoading, statuses } = useBoard();
  const { activeTask } = useDialogs();

  const {
    caseFiles,
    isLoading: areResourcesLoading,
    priorities,
    tags,
    users: assignees,
  } = useResources();

  const alertTitle = activeTask ? ERROR_TITLES.task.update : ERROR_TITLES.task.create;

  const assigneeOptions = useMemo(
    () => peopleOptions({ currentUserId: currentUser.id, users: assignees }),
    [assignees, currentUser.id]
  );

  const caseFileOptions = caseFiles.map(({ id, name }) => ({ label: name, value: id }));

  const priorityOptions = useMemo(
    () =>
      priorities.map(({ label, value }) => {
        const { color, icon: faIcon } = TASK_PRIORITIES[value || 'none'];

        return {
          icon: <FontAwesomeIcon className={color} icon={faIcon} />,
          label,
          value,
        };
      }),
    [priorities]
  );

  const statusOptions = statuses.map(({ id, name }) => ({ label: name, value: id }));

  const tagOptions = useMemo(
    () =>
      tags.map(({ color, id, name }) => ({
        icon: <TagSwatch color={color} />,
        label: name,
        value: id,
      })),
    [tags]
  );

  return (
    <ShadcnForm {...form}>
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-0.5">
          <InputField
            control={form.control}
            name="name"
            placeholder="Task name"
            variant="ghost"
          />

          <TextareaField
            control={form.control}
            name="notes"
            placeholder="Add a description..."
            variant="ghost"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <SelectChip
            control={form.control}
            disabled={areResourcesLoading}
            entity="case"
            icon={caseIcon}
            name="caseFileId"
            options={caseFileOptions}
          />

          <SelectChip
            control={form.control}
            disabled={areStatusesLoading}
            entity="status"
            icon={statusIcon}
            name="taskStatusId"
            options={statusOptions}
          />

          <SelectChip
            control={form.control}
            disabled={areResourcesLoading}
            entity="assignee"
            icon={assigneeIcon}
            name="assigneeId"
            options={assigneeOptions}
          />

          <SelectChip
            control={form.control}
            disabled={areResourcesLoading}
            entity="priority"
            icon={priorityIcon}
            name="priority"
            options={priorityOptions}
          />

          <MultiSelectField
            control={form.control}
            disabled={areResourcesLoading}
            entity="tag"
            icon={tagIcon}
            name="tagIds"
            options={tagOptions}
          />
        </div>

        {!isEmpty(errorMessage) && (
          <Alert
            description={errorMessage}
            icon={alertIcon}
            title={alertTitle}
            variant="destructive"
          />
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>

          <SubmitButton
            disabled={areResourcesLoading || areStatusesLoading}
            isLoading={isSubmitting}
          >
            Save
          </SubmitButton>
        </DialogFooter>
      </form>
    </ShadcnForm>
  );
};

Form.propTypes = {
  errorMessage: PropTypes.string.isRequired,
  form: formShape.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default Form;
